import React, { useState } from 'react';
import {
  Box,
  FormControl,
  TextField,
  Popover,
  Typography,
  Grid,
  Button,
  IconButton,
  alpha,
  Alert,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SEARCE_COLORS } from '../theme';
import { FILTER_UI_STANDARD } from '../config/filterUiStandard';
import { motion, AnimatePresence } from 'framer-motion';

interface MonthRangePickerProps {
  startMonth: { year: number; month: number } | null;
  endMonth: { year: number; month: number } | null;
  onChange: (start: { year: number; month: number } | null, end: { year: number; month: number } | null) => void;
  label?: string;
}

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MonthRangePicker: React.FC<MonthRangePickerProps> = ({
  startMonth,
  endMonth,
  onChange,
  label = 'Duration',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentViewYear, setCurrentViewYear] = useState<number>(
    startMonth?.year || new Date().getFullYear()
  );
  const [selectingStart, setSelectingStart] = useState<boolean>(true);
  const [showRangeRequiredAlert, setShowRangeRequiredAlert] = useState<boolean>(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (!startMonth) {
      setSelectingStart(true);
    } else if (!endMonth) {
      setSelectingStart(false);
    }
  };

  const handleClose = () => {
    // Check if both months are deselected
    if (!startMonth && !endMonth) {
      setShowRangeRequiredAlert(true);
      // Auto-hide alert after 3 seconds
      setTimeout(() => setShowRangeRequiredAlert(false), 3000);
      return; // Don't close the popover
    }
    setShowRangeRequiredAlert(false);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleMonthSelect = (year: number, month: number) => {
    // Prevent selection of future months
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-based
    // if (year > currentYear || (year === currentYear && month > currentMonth)) {
    //   return; // Don't allow selection of future months
    // }

    const clickedDate = new Date(year, month - 1);

    // First, check if clicking on an already selected start or end month to deselect it
    // This must be checked BEFORE any other logic
    const isClickingStartMonth = startMonth && startMonth.year === year && startMonth.month === month;
    const isClickingEndMonth = endMonth && endMonth.year === year && endMonth.month === month;

    if (isClickingStartMonth) {
      // Collapse to end month only (single-month range) when clicking the selected start
      if (endMonth) {
        onChange({ year: endMonth.year, month: endMonth.month }, { year: endMonth.year, month: endMonth.month });
      } else {
        onChange(null, null);
      }
      setSelectingStart(true);
      setShowRangeRequiredAlert(false);
      return;
    }

    if (isClickingEndMonth) {
      // Collapse to start month only (single-month range) when clicking the selected end
      if (startMonth) {
        onChange({ year: startMonth.year, month: startMonth.month }, { year: startMonth.year, month: startMonth.month });
      } else {
        onChange(null, null);
      }
      setSelectingStart(false);
      setShowRangeRequiredAlert(false);
      return;
    }

    // If we have a valid range (both start and end), handle range extension
    if (startMonth && endMonth) {
      const startDate = new Date(startMonth.year, startMonth.month - 1);
      const endDate = new Date(endMonth.year, endMonth.month - 1);

      // If clicking before the range, extend start
      if (clickedDate < startDate) {
        onChange({ year, month }, endMonth);
        setSelectingStart(true);
        return;
      }

      // If clicking after the range, extend end
      if (clickedDate > endDate) {
        onChange(startMonth, { year, month });
        setSelectingStart(false);
        return;
      }

      // If clicking within the range (but not on start or end), update based on which is closer
      // Note: This should not happen if we clicked on start/end (handled above), but keeping for safety
      const distToStart = Math.abs(clickedDate.getTime() - startDate.getTime());
      const distToEnd = Math.abs(clickedDate.getTime() - endDate.getTime());

      if (distToStart < distToEnd) {
        // Closer to start, update start
        onChange({ year, month }, endMonth);
        setSelectingStart(true);
      } else {
        // Closer to end, update end
        onChange(startMonth, { year, month });
        setSelectingStart(false);
      }
      return;
    }

    // If we only have start, set end
    if (startMonth && !endMonth) {
      const startDate = new Date(startMonth.year, startMonth.month - 1);
      if (clickedDate < startDate) {
        // If clicked before start, swap them
        onChange({ year, month }, { year: startMonth.year, month: startMonth.month });
        setSelectingStart(false);
      } else {
        onChange(startMonth, { year, month });
        setSelectingStart(false);
      }
      setShowRangeRequiredAlert(false);
      return;
    }

    // If we only have end, set start
    if (!startMonth && endMonth) {
      const endDate = new Date(endMonth.year, endMonth.month - 1);
      if (clickedDate > endDate) {
        // If clicked after end, swap them
        onChange({ year: endMonth.year, month: endMonth.month }, { year, month });
        setSelectingStart(true);
      } else {
        onChange({ year, month }, endMonth);
        setSelectingStart(true);
      }
      setShowRangeRequiredAlert(false);
      return;
    }

    // If we have neither, set start
    onChange({ year, month }, null);
    setSelectingStart(false);
    setShowRangeRequiredAlert(false);
  };

  const handleYearChange = (delta: number) => {
    setCurrentViewYear(prev => prev + delta);
  };

  const isMonthInRange = (year: number, month: number) => {
    if (!startMonth || !endMonth) return false;
    const date = new Date(year, month - 1);
    const startDate = new Date(startMonth.year, startMonth.month - 1);
    const endDate = new Date(endMonth.year, endMonth.month - 1);
    return date >= startDate && date <= endDate;
  };

  const isMonthSelected = (year: number, month: number, isStart: boolean) => {
    if (isStart && startMonth) {
      return startMonth.year === year && startMonth.month === month;
    }
    if (!isStart && endMonth) {
      return endMonth.year === year && endMonth.month === month;
    }
    return false;
  };

  const getDisplayText = () => {
    if (!startMonth && !endMonth) {
      return 'Select duration';
    }
    if (startMonth && !endMonth) {
      return `${monthNames[startMonth.month - 1]} ${startMonth.year} - ...`;
    }
    if (startMonth && endMonth) {
      const startDate = new Date(startMonth.year, startMonth.month - 1);
      const endDate = new Date(endMonth.year, endMonth.month - 1);
      const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth()) + 1;
      return `${monthNames[startMonth.month - 1]} ${startMonth.year} - ${monthNames[endMonth.month - 1]} ${endMonth.year} (${monthsDiff} ${monthsDiff === 1 ? 'month' : 'months'})`;
    }
    return 'Select duration';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl fullWidth size="small">
        <TextField
          size="small"
          label={label}
          value={getDisplayText()}
          onClick={handleOpen}
          InputProps={{
            readOnly: true,
            endAdornment: <CalendarIcon sx={{ color: SEARCE_COLORS.textSecondary, ml: 1, fontSize: '1rem' }} />,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              cursor: 'pointer',
              fontSize: FILTER_UI_STANDARD.controlFontSize,
              '&:hover': {
                borderColor: SEARCE_COLORS.primary,
              },
            },
            '& .MuiInputLabel-root': {
              fontSize: FILTER_UI_STANDARD.controlFontSize,
            },
            '& .MuiOutlinedInput-input': {
              fontSize: FILTER_UI_STANDARD.controlFontSize,
            },
          }}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              minWidth: 240,
              maxWidth: 280,
              p: 1.5,
            },
          }}
        >
          <Box>
            {/* Header with year navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <IconButton
                size="small"
                onClick={() => handleYearChange(-1)}
                sx={{ color: SEARCE_COLORS.primary, p: 0.5 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, fontSize: '0.95rem' }}>
                {currentViewYear}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleYearChange(1)}
                disabled={false}
                sx={{
                  color: SEARCE_COLORS.primary,
                  p: 0.5
                }}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Month grid */}
            <Grid container spacing={0.5}>
              {monthNames.map((monthName, index) => {
                const month = index + 1;
                // Check if this month is the start month
                const isStartMonth = startMonth && startMonth.year === currentViewYear && startMonth.month === month;
                // Check if this month is the end month
                const isEndMonth = endMonth && endMonth.year === currentViewYear && endMonth.month === month;
                const isSelected = isStartMonth || isEndMonth;
                const isInRange = isMonthInRange(currentViewYear, month);
                const isStart = isStartMonth;
                const isEnd = isEndMonth;

                // Disable future months (months beyond current month in current year, or any month in future years)
                // const currentDate = new Date();
                // const currentYear = currentDate.getFullYear();
                // const currentMonth = currentDate.getMonth() + 1; // 1-based
                // const isFutureMonth = currentViewYear > currentYear || 
                //                      (currentViewYear === currentYear && month > currentMonth);
                const isDisabled = false;

                return (
                  <Grid item xs={4} key={month}>
                    <AnimatePresence>
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Button
                          fullWidth
                          variant={isSelected ? 'contained' : isInRange ? 'outlined' : 'text'}
                          onClick={() => handleMonthSelect(currentViewYear, month)}
                          disabled={isDisabled}
                          sx={{
                            minHeight: 32,
                            fontSize: '0.7rem',
                            fontWeight: isSelected ? 600 : 400,
                            textTransform: 'none',
                            backgroundColor: isSelected
                              ? SEARCE_COLORS.primary
                              : isInRange
                                ? alpha(SEARCE_COLORS.primary, 0.1)
                                : 'transparent',
                            color: isDisabled
                              ? SEARCE_COLORS.neutral_300
                              : isSelected
                                ? SEARCE_COLORS.white
                                : isInRange
                                  ? SEARCE_COLORS.primary
                                  : SEARCE_COLORS.textPrimary,
                            borderColor: isInRange ? SEARCE_COLORS.primary : 'transparent',
                            borderWidth: isInRange ? 1 : 0,
                            borderStyle: 'solid',
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            '&:hover': {
                              backgroundColor: isDisabled
                                ? 'transparent'
                                : isSelected
                                  ? SEARCE_COLORS.primary
                                  : alpha(SEARCE_COLORS.primary, 0.15),
                              transform: isDisabled ? 'none' : 'scale(1.05)',
                            },
                            '&:disabled': {
                              opacity: 0.5,
                              color: SEARCE_COLORS.neutral_300,
                            },
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            '&::before': (isStart || isEnd) ? {
                              content: '""',
                              position: 'absolute',
                              top: -2,
                              left: -2,
                              right: -2,
                              bottom: -2,
                              borderRadius: 1,
                              border: `2px solid ${SEARCE_COLORS.primary}`,
                              animation: 'pulse 2s infinite',
                            } : {},
                            '@keyframes pulse': {
                              '0%, 100%': {
                                opacity: 1,
                              },
                              '50%': {
                                opacity: 0.5,
                              },
                            },
                          }}
                        >
                          {monthName.substring(0, 3)}
                        </Button>
                      </motion.div>
                    </AnimatePresence>
                  </Grid>
                );
              })}
            </Grid>

            {/* Alert when range is required */}
            {showRangeRequiredAlert && (
              <Alert
                severity="warning"
                sx={{ mt: 1.5, fontSize: '0.75rem', py: 0.5 }}
                onClose={() => setShowRangeRequiredAlert(false)}
              >
                Please select a range before closing the filter.
              </Alert>
            )}

            {/* Selected range display */}
            {(startMonth || endMonth) && (
              <Box sx={{ mt: 1.5, p: 1, bgcolor: alpha(SEARCE_COLORS.success, 0.1), borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: SEARCE_COLORS.success, mb: 0.25 }}>
                  Selected Range:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: SEARCE_COLORS.textSecondary }}>
                  {startMonth
                    ? `${monthNames[startMonth.month - 1]} ${startMonth.year}`
                    : 'Not selected'}
                  {' → '}
                  {endMonth
                    ? `${monthNames[endMonth.month - 1]} ${endMonth.year}`
                    : 'Not selected'}
                </Typography>
              </Box>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => {
                  onChange(null, null);
                  setSelectingStart(true);
                }}
                sx={{
                  borderColor: SEARCE_COLORS.neutral_300,
                  color: SEARCE_COLORS.textSecondary,
                  '&:hover': {
                    borderColor: SEARCE_COLORS.error,
                    color: SEARCE_COLORS.error,
                  },
                }}
              >
                Clear
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={handleClose}
                sx={{
                  backgroundColor: SEARCE_COLORS.primary,
                  '&:hover': {
                    backgroundColor: SEARCE_COLORS.primary,
                    opacity: 0.9,
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>
      </FormControl>
    </LocalizationProvider>
  );
};

export default MonthRangePicker;

